import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import { ChevronDown, Send, X } from "lucide-react";
import Drawer from "../../../components/Drawer";
import { OverlayGrayBackground } from "../../../components/OverlayGrayBackground";
import { LoaderSpinner } from "../../../components/LoaderSpinner";
import VendorAttachmentsSection from "./VendorAttachmentsSection";
import VendorDocumentLinksSection from "./VendorDocumentLinksSection";
import { validateDocumentLinks, normalizeDocumentLinks } from "../../../helpers/server/vendorDocumentLinks";
import VendorDepositFirstConfirmModal from "./VendorDepositFirstConfirmModal";
import { currencies, defaultVendorAddress } from "../../../consts/management/vendors/vendors";
import { validateEmail } from "../../../helpers/management/vendors";
import { AUDIT_HIDDEN_FIELDS, formatAuditFieldLabel, formatAuditValue } from "./vendorAuditFormat";
import {
    addVendorNoteRequest,
    cleanUpVendorForView,
    getVendorAuditLogRequest,
    getVendorByIdRequest,
    getVendorNotesRequest,
    upsertVendorsRequest,
    VendorsLoadingKeys,
} from "../../../slices/managementSlices/vendorsSlice";

const INPUT_CLASS = "border-input w-full min-w-0 rounded-md border bg-transparent px-3 py-1 shadow-xs transition-[color,box-shadow] outline-none h-8 text-[13px] disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-gray-50";
const TEXTAREA_CLASS = "border-input w-full rounded-md border bg-transparent px-3 py-2 shadow-xs transition-[color,box-shadow] outline-none text-[13px] min-h-[72px] resize-y";
const SECTION_TITLE_CLASS = "text-[11px] font-semibold uppercase tracking-wider text-gray-600 pb-1 border-b border-border";
const LABEL_CLASS = "text-[12px] font-medium text-foreground";

const DEFAULT_FORM_VALUES = {
    name: "",
    emails: "",
    accountNumber: "",
    internalNote: "",
    defaultNote: "",
    address1: "",
    address2: "",
    city: "",
    zip: "",
    currency: "USD",
    phone: "",
    paymentTerms: "",
    depositFirst: 0,
    depositSecond: 0,
    balance: 0,
    credit: 0,
    depositFirstDays: 0,
    depositSecondDays: 0,
    balanceDays: 0,
    creditDays: 0,
    shippingDays: 0,
    documentLinks: [],
};

const TERM_COLUMNS = [
    { label: "Deposit 1", percentKey: "depositFirst", daysKey: "depositFirstDays" },
    { label: "Deposit 2", percentKey: "depositSecond", daysKey: "depositSecondDays" },
    { label: "Balance", percentKey: "balance", daysKey: "balanceDays" },
    { label: "Credit", percentKey: "credit", daysKey: "creditDays" },
    { label: "Shipping", percentKey: null, daysKey: "shippingDays" },
];

function Section({ title, children }) {
    return (
        <section className="space-y-3">
            <h3 className={SECTION_TITLE_CLASS}>{title}</h3>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <div className={`space-y-1 ${className}`}>
            <label className={LABEL_CLASS}>{label}</label>
            {children}
        </div>
    );
}

function VendorEmailTags({ value, onChange, error }) {
    const [inputValue, setInputValue] = useState("");
    const emails = value ? value.split(",").map((e) => e.trim()).filter(Boolean) : [];

    const addEmail = () => {
        const email = inputValue.trim().replace(/,$/, "");
        if (email && validateEmail(email) && !emails.includes(email)) {
            onChange([...emails, email].join(","));
            setInputValue("");
        }
    };

    const removeEmail = (index) => {
        onChange(emails.filter((_, i) => i !== index).join(","));
    };

    return (
        <div className="space-y-2">
            {emails.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {emails.map((email, index) => (
                        <span
                            key={email}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-gray-200 text-[12px] text-gray-900"
                        >
                            {email}
                            <button
                                type="button"
                                className="ml-0.5 text-muted-foreground hover:text-foreground"
                                onClick={() => removeEmail(index)}
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addEmail();
                    }
                }}
                onBlur={addEmail}
                placeholder="Type email and press Enter"
                className={INPUT_CLASS}
            />
            {error && <p className="text-[12px] text-red-500">{error}</p>}
        </div>
    );
}

function formatNoteTimestamp(value) {
    if (!value) return "";
    try {
        return format(new Date(value), "MMM d 'at' h:mm a");
    } catch {
        return "";
    }
}

const VendorDetailSheet = ({
    vendorId,
    variant,
    isOpen,
    onClose,
    onSuccessSubmit,
    defaultEmails = [],
    accessVariant = "edit",
}) => {
    const dispatch = useDispatch();
    const { loadingKeys, loading, vendorForView, vendorNotes, vendorAuditLog } = useSelector((state) => state.vendors);
    const [quickNoteInput, setQuickNoteInput] = useState("");
    const [notesExpanded, setNotesExpanded] = useState(true);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [depositFirstConfirmOpen, setDepositFirstConfirmOpen] = useState(false);

    const {
        control,
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        clearErrors,
        formState: { errors },
    } = useForm({ defaultValues: DEFAULT_FORM_VALUES });

    const depositFirst = watch("depositFirst") || 0;
    const depositSecond = watch("depositSecond") || 0;
    const balance = watch("balance") || 0;
    const credit = watch("credit") || 0;
    const paymentTotal = Number(depositFirst) + Number(depositSecond) + Number(balance) + Number(credit);

    const teamEmails = variant === "edit" ? (vendorForView?.defaultEmails || defaultEmails) : defaultEmails;
    const displayVendorId = variant === "edit" ? (vendorForView?.vendorId || "") : "";

    const isLoadingVendor = variant === "edit" && loadingKeys.includes(VendorsLoadingKeys.vendorForView);
    const isLoadingNotes = loadingKeys.includes(VendorsLoadingKeys.vendorNotes);
    const isLoadingAuditLog = loadingKeys.includes(VendorsLoadingKeys.vendorAuditLog);
    const isAddingNote = loadingKeys.includes(VendorsLoadingKeys.addVendorNote);
    const isSaving = loading && loadingKeys.includes(VendorsLoadingKeys.upsertVendor);
    const canAddNotes = variant === "edit" && Boolean(vendorForView?.id);

    useEffect(() => {
        if (!isOpen) {
            dispatch(cleanUpVendorForView({}));
            setQuickNoteInput("");
            setNotesExpanded(true);
            setHistoryExpanded(false);
            setDepositFirstConfirmOpen(false);
        }
    }, [dispatch, isOpen]);

    useEffect(() => {
        if (variant === "create" && isOpen) {
            reset(DEFAULT_FORM_VALUES);
            clearErrors();
        }
    }, [variant, isOpen, reset, clearErrors]);

    useEffect(() => {
        if (vendorId && variant === "edit" && isOpen) {
            dispatch(getVendorByIdRequest({ id: vendorId }));
            dispatch(getVendorNotesRequest({ vendorId }));
        }
    }, [vendorId, variant, isOpen, dispatch]);

    useEffect(() => {
        if (variant === "edit" && isOpen && vendorForView?.id) {
            dispatch(getVendorAuditLogRequest({ vendorId: vendorForView.id }));
        }
    }, [variant, isOpen, vendorForView?.id, dispatch]);

    useEffect(() => {
        if (vendorForView && Object.keys(vendorForView).length > 0 && variant === "edit") {
            Object.entries(vendorForView).forEach(([key, value]) => {
                if (key !== "id" && key !== "vendorId" && key !== "defaultEmails" && key !== "uploadedFiles") {
                    setValue(key, value ?? "");
                }
            });
            setValue("documentLinks", vendorForView.documentLinks || []);
        }
    }, [vendorForView, variant, setValue]);

    const restrictNumberInput = (e) => {
        if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleInputChange = (field, value) => {
        const numeric = Math.max(0, Math.min(Number(value) || 0, 9999));
        setValue(field, numeric);
    };

    const handlePercentChange = (percentKey, value) => {
        const numeric = Math.max(0, Math.min(Number(value) || 0, 9999));

        if (percentKey === "depositFirst" && numeric === 100 && Number(depositFirst) !== 100) {
            setDepositFirstConfirmOpen(true);
            return;
        }

        handleInputChange(percentKey, numeric);
    };

    const confirmDepositFirst100 = () => {
        setValue("depositFirst", 100);
        setDepositFirstConfirmOpen(false);
    };

    const appendQuickNote = async () => {
        const trimmed = quickNoteInput.trim();
        if (!trimmed || !canAddNotes) return;

        try {
            await dispatch(addVendorNoteRequest({
                vendorId: vendorForView.id,
                note: trimmed,
            })).unwrap();
            setQuickNoteInput("");
        } catch {
            // toast handled in slice
        }
    };

    const buildEditVendorPayload = (data) => ({
        id: vendorForView.id,
        sh_legacy_id: vendorForView.vendorId,
        email: data.emails,
        vendor_name: data.name,
        account_number: data.accountNumber || null,
        internal_note: data.internalNote || null,
        default_po_note: data.defaultNote || null,
        document_links: normalizeDocumentLinks(data.documentLinks),
        currency: data.currency || null,
        payment_terms: data.paymentTerms || null,
        credit: Number(data.credit || 0),
        balance: Number(data.balance || 0),
        deposit_first: Number(data.depositFirst || 0),
        deposit_second: Number(data.depositSecond || 0),
        deposit_first_days: Number(data.depositFirstDays || 0),
        deposit_second_days: Number(data.depositSecondDays || 0),
        credit_days: Number(data.creditDays || 0),
        shipping_days: Number(data.shippingDays || 0),
        balance_days: Number(data.balanceDays || 0),
        modified: true,
        address: {
            ...defaultVendorAddress,
            name: data.name,
            zip: data.zip || "",
            city: data.city || "",
            phone: data.phone || "",
            state: data.state || "",
            country: data.country || "",
            address1: data.address1 || "",
            address2: data.address2 || "",
        },
    });

    const buildCreateVendorPayload = (data) => ({
        email: data.emails,
        vendor_name: data.name,
        account_number: data.accountNumber,
        internal_note: data.internalNote,
        default_po_note: data.defaultNote,
        document_links: normalizeDocumentLinks(data.documentLinks),
        payment_terms: data.paymentTerms,
        credit: Number(data.credit || 0),
        balance: Number(data.balance || 0),
        deposit_first: Number(data.depositFirst || 0),
        deposit_second: Number(data.depositSecond || 0),
        deposit_first_days: Number(data.depositFirstDays || 0),
        deposit_second_days: Number(data.depositSecondDays || 0),
        credit_days: Number(data.creditDays || 0),
        shipping_days: Number(data.shippingDays || 0),
        balance_days: Number(data.balanceDays || 0),
        modified: true,
        address: {
            ...defaultVendorAddress,
            name: data.name,
        },
    });

    const onSubmit = async (data) => {

        const vendorData = variant === "edit"
            ? buildEditVendorPayload(data)
            : buildCreateVendorPayload(data);

        try {
            await dispatch(upsertVendorsRequest({
                vendorData,
                updateSingleVendor: variant === "edit",
            })).unwrap();
            if (variant === "edit" && vendorForView?.id) {
                dispatch(getVendorAuditLogRequest({ vendorId: vendorForView.id }));
            }
            onSuccessSubmit?.();
        } catch (error) {
            console.error(error);
        }
    };

    const title = variant === "edit" ? "Vendor Details" : "Create Vendor";
    const formId = "vendor-detail-form";

    return (
        <Drawer
            size="xl"
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            submitButtonText={variant === "edit" ? "Save Changes" : "Create Vendor"}
            formId={formId}
            showActionButtons
            bodyClassName="px-0"
        >
            <div className="relative h-full">
                {(isSaving) && <OverlayGrayBackground />}
                {isLoadingVendor ? (
                    <div className="flex h-60 items-center justify-center">
                        <LoaderSpinner width="70" height="70" border="3" colorSpinner="black" />
                    </div>
                ) : (
                    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                        <Section title="Vendor Identity">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Vendor Name" className="col-span-2">
                                    <input
                                        type="text"
                                        placeholder="Company name"
                                        className={INPUT_CLASS}
                                        {...register("name", {
                                            required: "Vendor name is required",
                                        })}
                                    />
                                    {errors.name && (
                                        <p className="text-[12px] text-red-500">{errors.name.message}</p>
                                    )}
                                </Field>
                                <Field label="Vendor ID">
                                    <input
                                        type="text"
                                        readOnly
                                        disabled
                                        placeholder="Auto-generated"
                                        value={displayVendorId}
                                        className={INPUT_CLASS}
                                    />
                                </Field>
                                <Field label="Account Number">
                                    <input
                                        type="text"
                                        placeholder="e.g. Supplier - 001"
                                        className={INPUT_CLASS}
                                        {...register("accountNumber")}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section title="Contacts">
                            <Field label="Vendor Email(s)">
                                <Controller
                                    name="emails"
                                    control={control}
                                    rules={{
                                        validate: (val) =>
                                            val.split(",").filter(Boolean).length > 0
                                            || "At least one valid email is required",
                                    }}
                                    render={({ field }) => (
                                        <VendorEmailTags
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={errors.emails?.message}
                                        />
                                    )}
                                />
                            </Field>
                            {teamEmails?.length > 0 && (
                                <Field label="Kings Loot Team Emails">
                                    <div className="flex flex-wrap gap-1.5">
                                        {teamEmails.map((email) => (
                                            <span
                                                key={email}
                                                className="inline-flex items-center px-2 py-0.5 rounded-sm bg-gray-100 text-[11px] text-gray-700"
                                            >
                                                {email}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-[11px] text-gray-600 mt-1">
                                        Auto-added from{" "} Vendors &gt; Settings
                                    </p>
                                </Field>
                            )}
                            <Field label="Phone">
                                <input
                                    type="text"
                                    placeholder="Phone number"
                                    className={INPUT_CLASS}
                                    {...register("phone")}
                                />
                            </Field>
                        </Section>

                        <Section title="Address & Financial">
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Address" className="col-span-2">
                                    <input
                                        type="text"
                                        placeholder="Street address"
                                        className={INPUT_CLASS}
                                        {...register("address1")}
                                    />
                                </Field>
                                <Field label="Address 2" className="col-span-2">
                                    <input
                                        type="text"
                                        placeholder="Suite, unit, building"
                                        className={INPUT_CLASS}
                                        {...register("address2")}
                                    />
                                </Field>
                                <Field label="City">
                                    <input
                                        type="text"
                                        placeholder="City"
                                        className={INPUT_CLASS}
                                        {...register("city")}
                                    />
                                </Field>
                                <Field label="Zip / Postal Code">
                                    <input
                                        type="text"
                                        placeholder="Zip code"
                                        className={INPUT_CLASS}
                                        {...register("zip")}
                                    />
                                </Field>
                                <Field label="Currency" className="col-span-2">
                                    <Controller
                                        name="currency"
                                        control={control}
                                        render={({ field }) => (
                                            <select
                                                value={field.value || "USD"}
                                                onChange={field.onChange}
                                                className={`${INPUT_CLASS} h-9`}
                                            >
                                                {currencies.map((currency) => (
                                                    <option key={currency.value} value={currency.value}>
                                                        {currency.label}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                </Field>
                            </div>
                        </Section>
                        {variant === "edit" && vendorForView?.id && (
                            <Section title="Notes">
                                <div className="space-y-2">
                                    {vendorNotes.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setNotesExpanded((current) => !current)}
                                            className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                                        >
                                            <ChevronDown
                                                className={`w-3.5 h-3.5 transition-transform duration-150 ${notesExpanded ? "" : "-rotate-90"}`}
                                            />
                                            {notesExpanded ? "Hide notes" : "Show notes"}
                                        </button>
                                    )}
                                    {notesExpanded && (
                                        <div className="space-y-2 overflow-y-auto max-h-[240px]">
                                            {isLoadingNotes ? (
                                                <p className="text-[12px] text-muted-foreground italic py-3 text-center">
                                                    Loading notes...
                                                </p>
                                            ) : vendorNotes.length ? (
                                                vendorNotes.map((entry) => (
                                                    <div
                                                        key={entry.id}
                                                        className="group relative rounded-md bg-[oklch(0.97_0.002_260)] border border-[oklch(0.92_0.004_260)] px-3 py-2"
                                                    >
                                                        <div className="flex items-center justify-between gap-2 mb-0.5">
                                                            <span className="text-[11px] font-semibold text-foreground">
                                                                {entry.authorName || "Kings Loot Team"}
                                                            </span>
                                                            <span className="text-[10px] text-muted-foreground">
                                                                {formatNoteTimestamp(entry.createdAt || entry.created_at)}
                                                            </span>
                                                        </div>
                                                        <p className="text-[12px] text-foreground leading-relaxed whitespace-pre-wrap">
                                                            {entry.note}
                                                        </p>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-[12px] text-muted-foreground italic py-3 text-center">
                                                    {canAddNotes
                                                        ? "No notes yet. Add a quick note below."
                                                        : "Save the vendor first to add notes."}
                                                </p>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex items-end gap-2">
                                        <textarea
                                            value={quickNoteInput}
                                            onChange={(e) => setQuickNoteInput(e.target.value)}
                                            placeholder="Add a note..."
                                            rows={1}
                                            disabled={!canAddNotes || isAddingNote}
                                            className="flex-1 px-3 py-2 text-[13px] border border-input rounded-md bg-background resize-none placeholder:text-muted-foreground min-h-[36px] max-h-[100px] disabled:opacity-50"
                                            onKeyDown={(e) => {
                                                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                                                    e.preventDefault();
                                                    appendQuickNote();
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            disabled={!canAddNotes || !quickNoteInput.trim() || isAddingNote}
                                            onClick={appendQuickNote}
                                            className="p-3 flex items-center justify-center rounded-md bg-[oklch(0.13_0.005_260)] text-white hover:bg-[oklch(0.2_0.005_260)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
                                            title="Send (Cmd+Enter)"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </Section> 
                        )}
                        <Section title="PO & Internal Notes">
                            <Field label="Internal Note">
                                <textarea
                                    placeholder="Internal notes about this vendor..."
                                    className={TEXTAREA_CLASS}
                                    {...register("internalNote")}
                                />
                            </Field>
                            <Field label="Default PO Note">
                                <textarea
                                    placeholder="Default note included on purchase orders..."
                                    className={TEXTAREA_CLASS}
                                    {...register("defaultNote")}
                                />
                            </Field>
                        </Section>

                        <Section title="Payment & Net Terms">
                            <div className="grid grid-cols-5 gap-2">
                                {TERM_COLUMNS.map(({ label, percentKey, daysKey }) => {
                                    if (!percentKey) {
                                        return (
                                            <div key={label} className="space-y-1.5 flex flex-col justify-end">
                                                <div className="space-y-1">
                                                    <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide block">
                                                        {label}
                                                    </label>
                                                    <div className="flex flex-col items-center">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            value={watch(daysKey) || 0}
                                                            onChange={(e) => handleInputChange(daysKey, e.target.value)}
                                                            onKeyDown={restrictNumberInput}
                                                            onWheel={(e) => e.target.blur()}
                                                            className={`${INPUT_CLASS} h-7 text-[12px] text-center no-arrows-by-input`}
                                                        />
                                                        <span className="text-[10px] text-muted-foreground mt-0.5">days</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={label} className="space-y-1.5">
                                            <div className="space-y-1">
                                                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide block">
                                                    {label}
                                                </label>
                                                <div className="flex flex-col items-center">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        value={watch(percentKey) || 0}
                                                        onChange={(e) => handlePercentChange(percentKey, e.target.value)}
                                                        onKeyDown={restrictNumberInput}
                                                        onWheel={(e) => e.target.blur()}
                                                        className={`${INPUT_CLASS} h-7 text-[12px] text-center no-arrows-by-input`}
                                                    />
                                                    <span className="text-[10px] text-muted-foreground mt-0.5">%</span>
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <div className="flex flex-col items-center">
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        value={watch(daysKey) || 0}
                                                        onChange={(e) => handleInputChange(daysKey, e.target.value)}
                                                        onKeyDown={restrictNumberInput}
                                                        onWheel={(e) => e.target.blur()}
                                                        className={`${INPUT_CLASS} h-7 text-[12px] text-center no-arrows-by-input`}
                                                    />
                                                    <span className="text-[10px] text-muted-foreground mt-0.5">days</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-between pt-1">
                                <span className={`text-[11px] font-medium ${paymentTotal === 100 ? "text-green-600" : "text-gray-600"}`}>
                                    Total: {paymentTotal}%
                                </span>
                            </div>
                        </Section>

                        {variant === "edit" && vendorForView?.id && (
                            <Section title="Change History">
                                <div className="space-y-2">
                                    <button
                                        type="button"
                                        onClick={() => setHistoryExpanded((current) => !current)}
                                        className="flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <ChevronDown
                                            className={`w-3.5 h-3.5 transition-transform duration-150 ${historyExpanded ? "" : "-rotate-90"}`}
                                        />
                                        {historyExpanded ? "Hide history" : `Show history${vendorAuditLog.length ? ` (${vendorAuditLog.length})` : ""}`}
                                    </button>
                                    {historyExpanded && (
                                        <div className="space-y-2 overflow-y-auto max-h-[280px]">
                                            {isLoadingAuditLog ? (
                                                <p className="text-[12px] text-muted-foreground italic py-3 text-center">
                                                    Loading history...
                                                </p>
                                            ) : vendorAuditLog.length ? (
                                                vendorAuditLog.map((entry) => {
                                                    const visibleChanges = (entry.changes || []).filter(
                                                        (change) => !AUDIT_HIDDEN_FIELDS.has(change.field)
                                                    );
                                                    return (
                                                        <div
                                                            key={entry.id}
                                                            className="rounded-md bg-[oklch(0.97_0.002_260)] border border-[oklch(0.92_0.004_260)] px-3 py-2"
                                                        >
                                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                                <span className="text-[11px] font-semibold text-foreground">
                                                                    {entry.userName || "System"}
                                                                    {entry.action === "INSERT" && " created this vendor"}
                                                                    {entry.action === "DELETE" && " deleted this vendor"}
                                                                </span>
                                                                <span className="text-[10px] text-muted-foreground">
                                                                    {formatNoteTimestamp(entry.createdAt)}
                                                                </span>
                                                            </div>
                                                            {entry.action === "UPDATE" && (
                                                                <ul className="space-y-0.5">
                                                                    {visibleChanges.map((change) => (
                                                                        <li
                                                                            key={`${entry.id}-${change.field}`}
                                                                            className="text-[12px] text-foreground leading-relaxed"
                                                                        >
                                                                            <span className="font-medium">{formatAuditFieldLabel(change.field)}:</span>{" "}
                                                                            <span className="text-muted-foreground line-through break-all">{formatAuditValue(change.oldValue)}</span>
                                                                            {" \u2192 "}
                                                                            <span className="break-all">{formatAuditValue(change.newValue)}</span>
                                                                        </li>
                                                                    ))}
                                                                    {visibleChanges.length === 0 && (
                                                                        <li className="text-[12px] text-muted-foreground italic">
                                                                            Internal fields updated
                                                                        </li>
                                                                    )}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <p className="text-[12px] text-muted-foreground italic py-3 text-center">
                                                    No recorded changes yet.
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </Section>
                        )}

                        <Section title="Document Links">
                            <Controller
                                name="documentLinks"
                                control={control}
                                rules={{ validate: validateDocumentLinks }}
                                render={({ field }) => (
                                    <VendorDocumentLinksSection
                                        value={field.value}
                                        onChange={field.onChange}
                                        access={accessVariant}
                                        error={errors.documentLinks?.message}
                                    />
                                )}
                            />
                        </Section>

                        {variant === "edit" && vendorForView?.id && (
                            <Section title="Attachments">
                                <VendorAttachmentsSection
                                    access={accessVariant}
                                    files={vendorForView.uploadedFiles || []}
                                    vendorId={vendorForView.id}
                                />
                            </Section>
                        )}
                    </form>
                )}
            </div>

            <VendorDepositFirstConfirmModal
                isOpen={depositFirstConfirmOpen}
                onCancel={() => setDepositFirstConfirmOpen(false)}
                onConfirm={confirmDepositFirst100}
            />
        </Drawer>
    );
};

export default VendorDetailSheet;
